// ============================================================================
// ListToolbar.tsx — ЕДИНАЯ панель списка (SRP): первичные кнопки слева,
// поиск по центру (всегда, на всю ширину), вторичные кнопки справа.
// Когда кнопки не помещаются — вторичные сворачиваются в «Ещё» (портал,
// не обрезается), при совсем узкой области — первичные тоже (остаётся
// только важность 5). Измеряется СТРОКА целиком (её clientWidth ограничен) —
// при расширении окна кнопки ВОЗВРАЩАЮТСЯ (гистерезис, без мигания).
// ============================================================================

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Mic, Square, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { startStt, createVoiceDictationComposer } from '@/lib/stt';
import { useSttAvailability } from '@/lib/useSttAvailability';
import { useBreakpoint } from '@/hooks/useBreakpoint';

export interface ListToolbarItem {
  key: string;
  label: string;
  icon?: ReactNode;
  importance?: number;
  variant?: 'default' | 'outline' | 'danger' | 'secondary';
  active?: boolean;
  onClick?: () => void;
  visible?: boolean;
}

interface ListToolbarProps {
  primary?: ListToolbarItem[];
  secondary?: ListToolbarItem[];
  searchValue?: string;
  onSearchChange?: (v: string) => void;
  searchPlaceholder?: string;
  /** Показывать поиск (команда 'search' списка) */
  showSearch?: boolean;
  trailing?: ReactNode;
  className?: string;
  /** ГОЛОСОВОЙ ПОИСК: микрофон у поля поиска — распознанная речь
   *  вставляется в поле. Свойство списков всех видов. */
  voiceSearch?: boolean;
}

const ENTER_PX = 8;
const EXIT_PX = 72;

export function ListToolbar({ primary = [], secondary = [], searchValue, onSearchChange, searchPlaceholder, showSearch = true, trailing, className, voiceSearch = false }: ListToolbarProps) {
  const t = useT();
  const searchHint = searchPlaceholder ?? t('common.search_placeholder');
  const ref = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLDivElement | null>(null);
  const { isMobile } = useBreakpoint();
  // 0 — всё видно; 1 — вторичные в «Ещё»; 2 — первичные тоже (только важность 5)
  const [level, setLevel] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const fullWidthRef = useRef(0);

  const visiblePrimary = primary.filter((i) => i.visible !== false);
  const visibleSecondary = secondary.filter((i) => i.visible !== false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const avail = el.clientWidth;
      if (level === 0) {
        if (el.scrollWidth > avail + ENTER_PX) {
          fullWidthRef.current = el.scrollWidth;
          setLevel(1);
        }
      } else if (level === 1) {
        if (el.scrollWidth > avail + ENTER_PX) {
          fullWidthRef.current = el.scrollWidth;
          setLevel(2);
        } else if (avail > fullWidthRef.current + EXIT_PX) {
          setLevel(0);
        }
      } else if (avail > fullWidthRef.current + EXIT_PX) {
        setLevel(0);
      }
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [level, visiblePrimary.length, visibleSecondary.length]);

  const shownPrimary = level >= 2 ? visiblePrimary.filter((i) => (i.importance || 3) >= 5) : visiblePrimary;
  const hiddenPrimary = visiblePrimary.filter((i) => !shownPrimary.includes(i));
  const shownSecondary = level >= 1 ? [] : visibleSecondary;
  const hiddenSecondary = visibleSecondary.filter((i) => !shownSecondary.includes(i));
  const hidden = [...hiddenSecondary, ...hiddenPrimary];

  const toggleMenu = () => {
    if (!menuOpen) {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setMenuOpen((v) => !v);
  };

  // Меню не должно уходить за низ экрана (и быть ПОД StatusBar z-[10000]):
  // если не влезает — открываем ВВЕРХ от кнопки; z — поверх всего.
  useEffect(() => {
    if (!menuOpen || !menuPos) return;
    const el = menuRef.current;
    if (!el) return;
    const h = el.offsetHeight;
    if (menuPos.top + h > window.innerHeight - 8) {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setMenuPos({ top: Math.max(8, r.top - h - 4), right: menuPos.right });
    }
  }, [menuOpen, menuPos]);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const renderBtn = (it: ListToolbarItem) => (
    <Button
      key={it.key}
      size="sm"
      variant={it.active ? 'default' : (it.variant === 'danger' ? 'destructive' : it.variant || 'outline')}
      className={cn('shrink-0', it.active && 'bg-primary text-primary-foreground')}
      onClick={it.onClick}
      title={it.label}
    >
      {it.icon}
      {it.label}
    </Button>
  );

  return (
    <div ref={ref} className={cn('shrink-0 overflow-hidden border-b bg-muted/30', className)}>
      <div className="flex items-center gap-2 px-2 py-1">
        {/* Первичные — слева */}
        {shownPrimary.map(renderBtn)}
        {/* Поиск — по центру (desktop); на мобильном переносится на ОТДЕЛЬНУЮ
            строку ниже (иначе в узкой строке с кнопками нет места для ввода). */}
        {!isMobile && showSearch && (
          <>
            <div className="relative flex-1 min-w-[80px]">
              <input
                value={searchValue || ''}
                onChange={(e) => onSearchChange?.(e.target.value)}
                placeholder={searchHint}
                className="h-7 w-full text-xs rounded-md border bg-input px-2 pr-6"
              />
              {searchValue ? (
                <button type="button" className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" title={t('common.clear_search')} onClick={() => onSearchChange?.('')}>
                  <X className="h-3 w-3" />
                </button>
              ) : null}
            </div>
            {voiceSearch && <VoiceSearchMic onText={(t) => onSearchChange?.(t)} />}
          </>
        )}
        {/* Правый блок: вторичные кнопки + trailing + «Ещё». `ml-auto` прижимает
            блок (и «Ещё») к ПРАВОМУ краю панели команд; когда поиск показан
            inline (flex-1) он и так занимает центр, но при скрытом/мобильном
            поиске без прижима кнопки «уезжали» влево. */}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {shownSecondary.map(renderBtn)}
          {trailing}
          {hidden.length > 0 && (
            <div ref={btnRef} className="relative shrink-0">
              <Button size="sm" variant="outline" onClick={toggleMenu} title={t('common.more')}>
                <ChevronDown className="h-3.5 w-3.5" />
                {t('common.more')}
              </Button>
              {menuOpen && menuPos && createPortal(
                <>
                  <div className="fixed inset-0 z-[60]" onClick={() => setMenuOpen(false)} />
                  <div
                    ref={menuRef}
                    className="fixed z-[10100] min-w-44 rounded-md border bg-popover p-1 shadow-lg"
                    style={{ top: menuPos.top, right: menuPos.right }}
                  >
                    {hidden.map((it) => (
                      <button
                        key={it.key}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-accent text-left',
                          it.active && 'bg-accent font-medium',
                        )}
                        onClick={() => { setMenuOpen(false); it.onClick?.(); }}
                      >
                        {it.icon}
                        {it.label}
                      </button>
                    ))}
                  </div>
                </>,
                document.body,
              )}
            </div>
          )}
        </div>
      </div>
      {/* МОБИЛЬНАЯ вторая строка: поиск на всю ширину + голосовой ввод —
          под панелью кнопок (в узкой строке нет места для ввода). */}
      {isMobile && showSearch && (
        <div className="flex items-center gap-2 px-2 pb-1.5">
          <div className="relative flex-1 min-w-[80px]">
            <input
              value={searchValue || ''}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder={searchHint}
              className="h-7 w-full text-xs rounded-md border bg-input px-2 pr-6"
            />
            {searchValue ? (
              <button type="button" className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" title={t('common.clear_search')} onClick={() => onSearchChange?.('')}>
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>
          {voiceSearch && <VoiceSearchMic onText={(t) => onSearchChange?.(t)} />}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// VoiceSearchMic — ГОЛОСОВОЙ ПОИСК (свойство списков всех видов): микрофон
// у поля поиска. Распознанная речь (STT) ЗАМЕНЯЕТ содержимое поля поиска
// (композитор lib/stt: финалы фиксируются ОДИН раз, interim перезаписывается)
// и сразу фильтрует список. Доступность — единая проверка useSttAvailability
// (SSoT): НЕДОСТУПНО → кнопки нет вовсе (без неактивных заглушек).
// ============================================================================
function VoiceSearchMic({ onText }: { onText: (t: string) => void }) {
  const t = useT();
  const stt = useSttAvailability();
  const [listening, setListening] = useState(false);
  const sessionRef = useRef<{ stop: () => void } | null>(null);
  const composerRef = useRef<ReturnType<typeof createVoiceDictationComposer> | null>(null);

  // ЕДИНАЯ проверка доступности (SSoT — useSttAvailability): нужен микрофон И
  // SpeechRecognition; запрещённый микрофон → кнопки нет вовсе.
  if (!stt.canTranscribe) return null;

  const toggle = async () => {
    if (listening) {
      sessionRef.current?.stop();
      sessionRef.current = null;
      composerRef.current = null;
      setListening(false);
      return;
    }
    try {
      setListening(true);
      // База — пустая: голос ЗАМЕНЯЕТ запрос поиска (не дописывает к старому).
      composerRef.current = createVoiceDictationComposer('', onText);
      const session = await startStt({
        continuous: false,
        onText: (text, final) => composerRef.current?.push(text, final),
        onError: () => setListening(false),
      });
      sessionRef.current = session;
    } catch {
      setListening(false);
    }
  };

  return (
    <button
      type="button"
      className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${listening ? 'bg-destructive text-white animate-pulse' : 'hover:bg-muted/40'}`}
      title={listening ? t('common.stop_listening') : t('common.voice_search')}
      onClick={() => void toggle()}
    >
      {listening ? <Square className="h-3 w-3" /> : <Mic className="h-3.5 w-3.5" />}
    </button>
  );
}
